import React, { useCallback } from 'react';

import type { URLSearchParamsInit } from 'react-router-dom';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { twMerge } from 'tailwind-merge';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';

import OrganisationSettingsPageContainer from 'pages/organization/OrganisationSettingsPage/components/organisation-settings-page-container';
import LoadingPage from 'pages/LoadingPage';

import type { IntegrationProvider } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration';
import { useOrganisationSettingsIntegration } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration';

import organisationSettingsRoutes, {
  IntegrationSettingsBaseRoute,
} from '../organisation-settings-routes';

const localize = createLocale('organisationIntegrationSettingsLocales');

const IntegrationSettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const { integrations } = useOrganisationSettingsIntegration({});

  const navigateToIntegrationPage = useCallback(
    (page: string, params?: URLSearchParamsInit) => {
      const route = Object.values(organisationSettingsRoutes).find(
        (routeObj) => routeObj.name === page,
      );

      if (route) {
        const searchParams = createSearchParams(params);
        const path = {
          pathname: IntegrationSettingsBaseRoute + route.path,
          search: searchParams.toString(),
        };
        return navigate(path);
      }
      return null;
    },
    [navigate],
  );

  const integrationProviderContainerClassName = twMerge(
    'flex flex-col md:grid gap-6',
    integrations.providers.length > 1 && 'lg:grid-cols-2',
  );

  const integrationProviderImageContainerClassName = useCallback(
    (provider: IntegrationProvider) =>
      twMerge(
        'flex justify-center border-b',
        provider.hasValidCredentials ? 'bg-primary-100' : 'bg-red-100',
      ),
    [],
  );

  if (integrations.isLoading) return <LoadingPage />;

  return (
    <OrganisationSettingsPageContainer
      title={localize('integrations')}
      selectedRoute={{
        groupName: 'organisation',
        name: 'integrations',
      }}
    >
      <div className="mt-8">
        <div className={integrationProviderContainerClassName}>
          {integrations.providers.map((provider) => (
            <div
              key={provider.id}
              className="overflow-hidden border rounded-lg"
            >
              <div
                className={integrationProviderImageContainerClassName(provider)}
              >
                <img
                  alt={provider.name}
                  src={`${window.STATIC_URL}logos/integrationProviders${provider.image}`}
                  className="h-8 md:h-12 my-4"
                />
              </div>
              <div className="m-4">
                <Typography variant="body-large-bold" as="h3" className="mb-2">
                  {provider.name}
                </Typography>
                <Typography variant="body-large" as="p">
                  {provider.description}
                </Typography>
                {provider.hasValidCredentials ? (
                  <Button
                    variant="primary"
                    label={localize('fetchJobs')}
                    className="mt-3"
                    size="medium"
                    onClick={() => navigateToIntegrationPage(provider.page)}
                  />
                ) : (
                  <Button
                    variant="secondary"
                    label={localize('connectWith', { provider: provider.name })}
                    className="mt-3"
                    size="medium"
                    onClick={() =>
                      navigateToIntegrationPage(provider.page, {
                        editApiView: 'true',
                      })
                    }
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </OrganisationSettingsPageContainer>
  );
};
export default IntegrationSettingsPage;
